import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdvisorSearch } from './use-advisor-search';

const schema = z.object({
  advisorQuery: z.string().min(1, 'Advisor ID or Email is required'),
});

// Searches an advisor by ID or email and keeps `?advisorId` in the URL, so a search made in one
// admin section carries over to the next.
export function useAdvisorLookup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryRef = useRef(searchParams.get('advisorId') ?? '');
  const search = useAdvisorSearch();
  const { searchAdvisor } = search;

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { advisorQuery: initialQueryRef.current },
  });

  useEffect(() => {
    if (initialQueryRef.current) {
      searchAdvisor(initialQueryRef.current);
    }
  }, [searchAdvisor]);

  const onSearch = form.handleSubmit(({ advisorQuery }) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('advisorId', advisorQuery);
        return next;
      },
      { replace: true },
    );
    searchAdvisor(advisorQuery);
  });

  return { ...search, register: form.register, errors: form.formState.errors, onSearch };
}
